import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../../../UserContext';
import { getSubscriptionLabel, hasPaidPlan } from '../../../../utils/subscriptionStatus';
import defaultProfile from '../../../../assets/images/dashboard-header-profile.jpg';
import { 
    Users, 
    Search, 
    Shield, 
    ShieldOff,
    CheckCircle,
    XCircle,
    Trash2, 
    ChevronLeft,
    ChevronRight,
    ArrowLeft,
    AlertTriangle,
    Crown,
    Calendar,
    Building2,
    UserCheck,
    Filter,
    X,
    Edit3,
    Send
} from 'lucide-react';
import './Admin.css';
import { adminService } from '../../../../services';

const AdminUsers = () => {
    const { user: currentUser } = useUser();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 20,
        totalCount: 0,
        totalPages: 0
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        isAdmin: null,
        isVerified: null,
        subscriptionStatus: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState('CreatedAt');
    const [sortDescending, setSortDescending] = useState(true);
    const [editingUser, setEditingUser] = useState(null);
    const [savingUserDetails, setSavingUserDetails] = useState(false);
    const [userForm, setUserForm] = useState({
        userName: '',
        email: '',
        firstName: '',
        lastName: '',
        mobileNumber: '',
        isAdmin: false,
        verified: false
    });
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [showCommunicationModal, setShowCommunicationModal] = useState(false);
    const [sendingCommunication, setSendingCommunication] = useState(false);
    const [communicationForm, setCommunicationForm] = useState({
        subject: '',
        content: '',
        emailOnlyOptedIn: true
    });

    const fetchUsers = useCallback(async () => {
        try {
            setIsLoading(true);
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                pageSize: pagination.pageSize.toString(),
                sortBy,
                sortDescending: sortDescending.toString()
            });

            if (searchTerm) params.append('searchTerm', searchTerm);
            if (filters.isAdmin !== null) params.append('isAdmin', filters.isAdmin.toString());
            if (filters.isVerified !== null) params.append('isVerified', filters.isVerified.toString());
            if (filters.subscriptionStatus) params.append('subscriptionStatus', filters.subscriptionStatus);

            const data = await adminService.getUsers(params);
            
            setUsers(data.users);
            setPagination(prev => ({
                ...prev,
                totalCount: data.totalCount,
                totalPages: data.totalPages
            }));
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [pagination.page, pagination.pageSize, searchTerm, filters, sortBy, sortDescending]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchUsers();
    };

    const handleToggleAdmin = async (userId) => {
        if (userId === currentUser?.userId) {
            setError("You cannot modify your own admin status");
            return;
        }

        try {
            await adminService.toggleUserAdmin(userId);
            await fetchUsers();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleToggleVerified = async (userId) => {
        try {
            await adminService.toggleUserVerified(userId);
            await fetchUsers();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        if (userId === currentUser?.id) {
            setError("You cannot delete your own account from the admin panel");
            return;
        }

        if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await adminService.deleteUser(userId);
            await fetchUsers();
        } catch (err) {
            setError(err.message);
        }
    };

    const clearFilters = () => {
        setFilters({
            isAdmin: null,
            isVerified: null,
            subscriptionStatus: ''
        });
        setSearchTerm('');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const toggleUserSelection = (userId) => {
        setSelectedUserIds((prev) => (
            prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
        ));
    };

    const toggleSelectPage = () => {
        const pageUserIds = users.map((user) => user.id);
        const allSelected = pageUserIds.every((id) => selectedUserIds.includes(id));
        setSelectedUserIds((prev) => {
            if (allSelected) {
                return prev.filter((id) => !pageUserIds.includes(id));
            }

            const next = new Set(prev);
            pageUserIds.forEach((id) => next.add(id));
            return Array.from(next);
        });
    };

    const openEditUser = (user) => {
        setEditingUser(user);
        setUserForm({
            userName: user.userName || '',
            email: user.email || '',
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            mobileNumber: user.mobileNumber || '',
            isAdmin: Boolean(user.isAdmin),
            verified: Boolean(user.verified)
        });
    };

    const closeEditUser = () => {
        setEditingUser(null);
        setSavingUserDetails(false);
    };

    const handleUserFormChange = (field, value) => {
        setUserForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveUserDetails = async (event) => {
        event.preventDefault();
        if (!editingUser) return;

        setSavingUserDetails(true);
        try {
            const updatedUser = await adminService.updateUserDetails(editingUser.id, {
                userName: userForm.userName.trim(),
                email: userForm.email.trim(),
                firstName: userForm.firstName.trim(),
                lastName: userForm.lastName.trim(),
                mobileNumber: userForm.mobileNumber.trim(),
                isAdmin: userForm.isAdmin,
                verified: userForm.verified
            });

            setUsers((prev) => prev.map((existing) => existing.id === updatedUser.id ? updatedUser : existing));
            closeEditUser();
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to update user details.');
        } finally {
            setSavingUserDetails(false);
        }
    };

    const hasActiveFilters = () => {
        return filters.isAdmin !== null || 
               filters.isVerified !== null || 
               filters.subscriptionStatus !== '' ||
               searchTerm !== '';
    };

    const isTextSort = ['UserName', 'Email', 'FirstName', 'LastName'].includes(sortBy);
    const orderPrimaryLabel = isTextSort ? 'A to Z' : 'Newest First';
    const orderSecondaryLabel = isTextSort ? 'Z to A' : 'Oldest First';
    const pageUserIds = users.map((user) => user.id);
    const allPageUsersSelected = pageUserIds.length > 0 && pageUserIds.every((id) => selectedUserIds.includes(id));

    const openCommunicationModal = () => {
        if (selectedUserIds.length === 0) {
            setError('Select at least one user before sending a targeted communication.');
            return;
        }
        setError(null);
        setShowCommunicationModal(true);
    };

    const closeCommunicationModal = () => {
        setShowCommunicationModal(false);
        setSendingCommunication(false);
        setCommunicationForm({
            subject: '',
            content: '',
            emailOnlyOptedIn: true
        });
    };

    const handleSendCommunication = async (event) => {
        event.preventDefault();
        if (!communicationForm.subject.trim() || !communicationForm.content.trim()) {
            setError('Subject and message are required.');
            return;
        }
        if (selectedUserIds.length === 0) {
            setError('Select at least one user before sending a targeted communication.');
            return;
        }

        setSendingCommunication(true);
        try {
            const result = await adminService.sendTargetedCommunication({
                subject: communicationForm.subject.trim(),
                content: communicationForm.content.trim(),
                userIds: selectedUserIds,
                emailOnlyOptedIn: communicationForm.emailOnlyOptedIn
            });

            setError(null);
            closeCommunicationModal();
            setSelectedUserIds([]);
            setSuccessMessage(`Targeted communication sent: ${result.deliveredCount} delivered, ${result.failedCount} failed.`);
        } catch (err) {
            setError(err.message || 'Failed to send targeted communication.');
        } finally {
            setSendingCommunication(false);
        }
    };

    if (!currentUser?.isAdmin) {
        return (
            <div className="admin-page">
                <div className="admin-access-denied">
                    <AlertTriangle size={48} />
                    <h2>Access Denied</h2>
                    <p>You do not have permission to access this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <div className="admin-header-content">
                    <Link to="/dashboard/admin" className="admin-back-btn">
                        <ArrowLeft size={20} />
                    </Link>
                    <div className="admin-header-icon">
                        <Users size={28} />
                    </div>
                    <div>
                        <h1>Manage Users</h1>
                        <p>{pagination.totalCount} total users</p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="admin-alert admin-alert-error">
                    <AlertTriangle size={20} />
                    {error}
                    <button onClick={() => setError(null)}><X size={16} /></button>
                </div>
            )}

            {successMessage && (
                <div className="admin-alert admin-alert-success">
                    <CheckCircle size={20} />
                    {successMessage}
                    <button onClick={() => setSuccessMessage(null)}><X size={16} /></button>
                </div>
            )}

            <div className="admin-users-controls">
                <form onSubmit={handleSearch} className="admin-search-form">
                    <div className="admin-search-input">
                        <Search size={20} />
                        <input
                            type="text"
                            placeholder="Search by name, email, or username..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="admin-btn admin-btn-primary">
                        Search
                    </button>
                    <button 
                        type="button" 
                        onClick={() => setShowFilters(!showFilters)}
                        className={`admin-btn admin-btn-secondary ${hasActiveFilters() ? 'has-filters' : ''}`}
                    >
                        <Filter size={18} />
                        Filters
                        {hasActiveFilters() && <span className="filter-badge" />}
                    </button>
                </form>

                <div className="admin-selection-row">
                    <span>{selectedUserIds.length} selected</span>
                    <button
                        type="button"
                        className="admin-btn admin-btn-primary"
                        onClick={openCommunicationModal}
                        disabled={selectedUserIds.length === 0}
                    >
                        <Send size={16} />
                        Targeted Email
                    </button>
                </div>

                {showFilters && (
                    <div className="admin-filters-panel">
                        <div className="admin-filter-group">
                            <label>Admin Status</label>
                            <select 
                                value={filters.isAdmin === null ? '' : filters.isAdmin.toString()}
                                onChange={(e) => setFilters(prev => ({
                                    ...prev,
                                    isAdmin: e.target.value === '' ? null : e.target.value === 'true'
                                }))}
                            >
                                <option value="">All</option>
                                <option value="true">Admins Only</option>
                                <option value="false">Non-Admins Only</option>
                            </select>
                        </div>
                        <div className="admin-filter-group">
                            <label>Verified Status</label>
                            <select 
                                value={filters.isVerified === null ? '' : filters.isVerified.toString()}
                                onChange={(e) => setFilters(prev => ({
                                    ...prev,
                                    isVerified: e.target.value === '' ? null : e.target.value === 'true'
                                }))}
                            >
                                <option value="">All</option>
                                <option value="true">Verified Only</option>
                                <option value="false">Unverified Only</option>
                            </select>
                        </div>
                        <div className="admin-filter-group">
                            <label>Subscription</label>
                            <select 
                                value={filters.subscriptionStatus}
                                onChange={(e) => setFilters(prev => ({
                                    ...prev,
                                    subscriptionStatus: e.target.value
                                }))}
                            >
                                <option value="">All</option>
                                <option value="Premium">Premium</option>
                                <option value="Venue">Venue</option>
                                <option value="Free">Free</option>
                            </select>
                        </div>
                        <div className="admin-filter-group">
                            <label>Sort By</label>
                            <select 
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="CreatedAt">Join Date</option>
                                <option value="LastLoginAt">Last Login</option>
                                <option value="UserName">Username</option>
                                <option value="Email">Email</option>
                                <option value="FirstName">First Name</option>
                                <option value="LastName">Last Name</option>
                            </select>
                        </div>
                        <div className="admin-filter-group">
                            <label>Order</label>
                            <select 
                                value={sortDescending.toString()}
                                onChange={(e) => setSortDescending(e.target.value === 'true')}
                            >
                                <option value="false">{orderPrimaryLabel}</option>
                                <option value="true">{orderSecondaryLabel}</option>
                            </select>
                        </div>
                        {hasActiveFilters() && (
                            <button onClick={clearFilters} className="admin-btn admin-btn-text">
                                Clear Filters
                            </button>
                        )}
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="admin-loading">
                    <div className="admin-spinner"></div>
                    <p>Loading users...</p>
                </div>
            ) : users.length === 0 ? (
                <div className="admin-empty-state">
                    <Users size={64} />
                    <h3>No Users Found</h3>
                    <p>Try adjusting your search or filters.</p>
                    {hasActiveFilters() && (
                        <button onClick={clearFilters} className="admin-btn admin-btn-primary">
                            Clear Filters
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="admin-users-table-container">
                        <table className="admin-users-table">
                            <thead>
                                <tr>
                                    <th>
                                        <input
                                            type="checkbox"
                                            checked={allPageUsersSelected}
                                            onChange={toggleSelectPage}
                                            aria-label="Select all users on this page"
                                        />
                                    </th>
                                    <th>User</th>
                                    <th>Status</th>
                                    <th>Subscription</th>
                                    <th>Activity</th>
                                    <th>Joined</th>
                                    <th>Last Login</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user.id} className={user.id === currentUser?.userId ? 'current-user' : ''}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedUserIds.includes(user.id)}
                                                onChange={() => toggleUserSelection(user.id)}
                                                aria-label={`Select ${user.userName}`}
                                            />
                                        </td>
                                        <td>
                                            <div className="admin-user-cell">
                                                <img 
                                                    src={user.profilePictureUrl || defaultProfile} 
                                                    alt={user.userName}
                                                    className="admin-user-avatar"
                                                />
                                                <div className="admin-user-info">
                                                    <strong>{user.firstName} {user.lastName}</strong>
                                                    <span className="admin-user-username">@{user.userName}</span>
                                                    <span className="admin-user-email">{user.email}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="admin-user-badges">
                                                {user.isAdmin && (
                                                    <span className="admin-badge admin-badge-admin">
                                                        <Shield size={14} /> Admin
                                                    </span>
                                                )}
                                                {user.verified ? (
                                                    <span className="admin-badge admin-badge-verified">
                                                        <CheckCircle size={14} /> Verified
                                                    </span>
                                                ) : (
                                                    <span className="admin-badge admin-badge-unverified">
                                                        <XCircle size={14} /> Unverified
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`admin-subscription-badge ${hasPaidPlan(user.subscriptionStatus) ? 'premium' : 'free'}`}>
                                                {hasPaidPlan(user.subscriptionStatus) && <Crown size={14} />}
                                                {getSubscriptionLabel(user.subscriptionStatus)}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="admin-user-stats">
                                                <span title="Events">
                                                    <Calendar size={14} /> {user.totalEvents}
                                                </span>
                                                <span title="Friends">
                                                    <UserCheck size={14} /> {user.totalFriends}
                                                </span>
                                                {user.clubAdminCount > 0 && (
                                                    <span title="Venue Admin">
                                                        <Building2 size={14} /> {user.clubAdminCount}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="admin-date">
                                                {new Date(user.createdAt).toLocaleDateString()}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="admin-date">
                                                {user.lastLoginAt 
                                                    ? new Date(user.lastLoginAt).toLocaleDateString()
                                                    : 'Never'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="admin-user-actions">
                                                <button
                                                    onClick={() => openEditUser(user)}
                                                    className="admin-btn admin-btn-icon"
                                                    title="Edit User"
                                                >
                                                    <Edit3 size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleToggleAdmin(user.id)}
                                                    className={`admin-btn admin-btn-icon ${user.isAdmin ? 'active' : ''}`}
                                                    title={user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                                                    disabled={user.id === currentUser?.userId}
                                                >
                                                    {user.isAdmin ? <ShieldOff size={18} /> : <Shield size={18} />}
                                                </button>
                                                <button
                                                    onClick={() => handleToggleVerified(user.id)}
                                                    className={`admin-btn admin-btn-icon ${user.verified ? 'active' : ''}`}
                                                    title={user.verified ? 'Unverify User' : 'Verify User'}
                                                >
                                                    {user.verified ? <XCircle size={18} /> : <CheckCircle size={18} />}
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteUser(user.id, user.userName)}
                                                    className="admin-btn admin-btn-icon admin-btn-danger"
                                                    title="Delete User"
                                                    disabled={user.id === currentUser?.userId}
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination.totalPages > 1 && (
                        <div className="admin-pagination">
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                disabled={pagination.page === 1}
                                className="admin-btn admin-btn-icon"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <span className="admin-pagination-info">
                                Page {pagination.page} of {pagination.totalPages}
                            </span>
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                disabled={pagination.page === pagination.totalPages}
                                className="admin-btn admin-btn-icon"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    )}
                </>
            )}

            {editingUser && (
                <div className="admin-modal-overlay" onClick={closeEditUser}>
                    <div className="admin-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2>Edit User Details</h2>
                            <button className="admin-modal-close" onClick={closeEditUser}>
                                <X size={20} />
                            </button>
                        </div>
                        <form className="admin-modal-form" onSubmit={handleSaveUserDetails}>
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Username</label>
                                    <input
                                        value={userForm.userName}
                                        onChange={(e) => handleUserFormChange('userName', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        value={userForm.email}
                                        onChange={(e) => handleUserFormChange('email', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>First Name</label>
                                    <input
                                        value={userForm.firstName}
                                        onChange={(e) => handleUserFormChange('firstName', e.target.value)}
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Last Name</label>
                                    <input
                                        value={userForm.lastName}
                                        onChange={(e) => handleUserFormChange('lastName', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="admin-form-group">
                                <label>Mobile Number</label>
                                <input
                                    value={userForm.mobileNumber}
                                    onChange={(e) => handleUserFormChange('mobileNumber', e.target.value)}
                                    placeholder="+44..."
                                />
                            </div>
                            <div className="admin-form-row">
                                <label className="admin-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={userForm.isAdmin}
                                        onChange={(e) => handleUserFormChange('isAdmin', e.target.checked)}
                                        disabled={editingUser.id === currentUser?.userId}
                                    />
                                    <span>Admin Access</span>
                                </label>
                                <label className="admin-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={userForm.verified}
                                        onChange={(e) => handleUserFormChange('verified', e.target.checked)}
                                    />
                                    <span>Verified User</span>
                                </label>
                            </div>
                            <div className="admin-modal-actions">
                                <button type="button" className="admin-btn admin-btn-secondary" onClick={closeEditUser}>
                                    Cancel
                                </button>
                                <button type="submit" className="admin-btn admin-btn-primary" disabled={savingUserDetails}>
                                    {savingUserDetails ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showCommunicationModal && (
                <div className="admin-modal-overlay" onClick={closeCommunicationModal}>
                    <div className="admin-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2>Send Targeted Communication</h2>
                            <button className="admin-modal-close" onClick={closeCommunicationModal}>
                                <X size={20} />
                            </button>
                        </div>
                        <form className="admin-modal-form" onSubmit={handleSendCommunication}>
                            <div className="admin-form-group">
                                <label>Recipients</label>
                                <p className="admin-form-helper">
                                    {selectedUserIds.length} selected user{selectedUserIds.length === 1 ? '' : 's'} from Manage Users.
                                </p>
                            </div>
                            <div className="admin-form-group">
                                <label>Subject</label>
                                <input
                                    value={communicationForm.subject}
                                    onChange={(e) => setCommunicationForm((prev) => ({ ...prev, subject: e.target.value }))}
                                    maxLength={140}
                                    required
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Message</label>
                                <textarea
                                    value={communicationForm.content}
                                    onChange={(e) => setCommunicationForm((prev) => ({ ...prev, content: e.target.value }))}
                                    rows={7}
                                    maxLength={5000}
                                    required
                                />
                            </div>
                            <label className="admin-checkbox" style={{ marginBottom: '20px' }}>
                                <input
                                    type="checkbox"
                                    checked={communicationForm.emailOnlyOptedIn}
                                    onChange={(e) => setCommunicationForm((prev) => ({ ...prev, emailOnlyOptedIn: e.target.checked }))}
                                />
                                <span>Email only opted-in users</span>
                            </label>
                            <div className="admin-modal-actions">
                                <button type="button" className="admin-btn admin-btn-secondary" onClick={closeCommunicationModal}>
                                    Cancel
                                </button>
                                <button type="submit" className="admin-btn admin-btn-primary" disabled={sendingCommunication}>
                                    {sendingCommunication ? 'Sending...' : 'Send Communication'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsers;
